import { inject, injectable } from 'inversify';
import { ReplaySubject, firstValueFrom, interval } from 'rxjs';
import { take } from 'rxjs/operators';
import {
    flatten,
    uniq
} from '../utils/lodash-min';

import { FileUpload } from '../models/file-upload';
import { FileUploadStatusTracker } from '../models/file-upload-status-tracker';
import { ProcessStageSubStateTypes, ProcessStageTypes, WebsocketResponse } from '../types/types';
import { JSONUtils } from '../utils/json.utils';
import { ApiConfig } from './api-config';
import { LightHttp } from './light-http.service';
import { ConnectivityUtils } from '../utils/connectivity.utils';
import { ProcessCompleteCheckService } from './process-complete-check.service';
import { IFileManagerService } from './service-interfaces';
import { SettingsService } from './settings.service';
import { StorageKeyPrefixeTypes, StorageService } from './storage.service';
import { UIContext } from './ui-context';
import { WebSocketService } from './websocket.service';

const FILE_DELETE_ENDPOINT = '/file-manager/delete/';

@injectable()
export class FileManagerService implements IFileManagerService {
    public readonly instanceId = Math.random().toString(36).substring(7);
    logger = {
        error: (...args) => console.log,
        trace7: (...args) => console.log,
    };

    private fileUploadTrackers: FileUploadStatusTracker[] = [];

    private _fileUploadsListChanged: ReplaySubject<FileUpload[]> = new ReplaySubject(1, Infinity);
    get fileUploadsListChanged() {
        return this._fileUploadsListChanged.asObservable();
    }

    private _fileUploadChanged: ReplaySubject<FileUpload> = new ReplaySubject(1);
    get fileUploadChanged() {
        return this._fileUploadChanged.asObservable();
    }

    private fileTrackerInitialized = false;
    private useWebsockets = false;
    private useTaskScheduling = false;
    private podTracker: FileUploadStatusTracker | null = null;

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(StorageService) private storageService: StorageService,
        @inject(UIContext) private uiContext: UIContext,
        @inject(ProcessCompleteCheckService) private processCompleteCheckService: ProcessCompleteCheckService,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(SettingsService) private settingsService: SettingsService,
    ) {
        this.settingsService.featureFlags.subscribe(ff => {
            const prevUseWebsockets = this.useWebsockets;
            this.useWebsockets = ff?.useWebsockets ?? false;
            this.useTaskScheduling = ff?.useTaskScheduling ?? false;
            console.log('[FileManager] Settings loaded: useTaskScheduling=', this.useTaskScheduling, 'useWebsockets=', this.useWebsockets);
            if (this.useTaskScheduling) {
                this.ensurePodTracker();
                // Move any files already in per-edge/untracked trackers to the pod tracker
                this.migrateFilesToPodTracker();
            }
            if (this.useWebsockets && !prevUseWebsockets) {
                this.initFileTrackingUsingWebSocket();
            }
            if (!this.useWebsockets || this.useTaskScheduling) {
                this.initFileTracker();
            }
        });
    }

    private initFileTrackingUsingWebSocket() {
        this.wsService.getWebsocketMessageObserver().subscribe({
            next: message => {
                if ((message as any).type === 'FALLBACK_TO_POLLING') {
                    this.switchToPolling();
                    return;
                }
                this.applyHistory(null, message);
            },
            error: err => this.uiContext.handleError(err),
        });
    }

    /**
     * Switch from websocket to HTTP polling mid-flight.
     * Triggered by FALLBACK_TO_POLLING signal from falcon when useWebsockets is toggled off.
     * Registers all in-flight file uploads with the tracker so replay-history picks them up.
     */
    private switchToPolling() {
        this.useWebsockets = false;
        const inFlightUploads = this.getFileUploads().filter(f => !(f.isCompleted() || f.isFailed()));
        this.initFileTracker();
        inFlightUploads.forEach(f => {
            if (!this.getFileUploadTracker(f)) {
                const tracker = FileUploadStatusTracker.createFromFileUpload(f);
                if (tracker) {
                    this.fileUploadTrackers.push(tracker);
                    tracker.addFileUpload(f);
                }
            }
        });
    }

    private initFileTracker() {
        if (!this.fileTrackerInitialized) {
            this.fileTrackerInitialized = true;
            let isRequestInProgress = false;
            interval(1000).subscribe(
                async () => {
                    if (!isRequestInProgress) {
                        isRequestInProgress = true;

                        const promiseArr = [];
                        if (this.fileUploadTrackers.length > 0) {
                            for (const tracker of this.fileUploadTrackers) {
                                promiseArr.push(tracker.replayHistory(
                                    this.http,
                                    (fileUpload: FileUpload, websocketResponse: WebsocketResponse) => {
                                        this.applyHistory(fileUpload, websocketResponse)
                                    }),
                                );
                            }
                        }
                        await Promise.all(promiseArr);
                        isRequestInProgress = false;
                    }
                }
            );
        }
    }

    public async removeFile(fileUpload: FileUpload) {
        let removed = false;
        for (const tracker of this.fileUploadTrackers) {
            if (tracker.hasFileUpload(fileUpload.id)) {
                tracker.removeFileUpload(fileUpload.id);
                removed = true;
            }
        }
        if (!removed) {
            console.error('File does not exist on any Tracker');
        }
        this.notifyFileUploadsListChanged();
        this.removeStoredFileUpload(fileUpload);
        this.removeRemoteFileAsync(fileUpload);
    }

    private removeStoredFileUpload(fileUpload: FileUpload) {
        this.storageService.remove(StorageKeyPrefixeTypes.FILE_UPLOAD_KEY_PREFIX, fileUpload.id);
    }

    private removeRemoteFileAsync(fileUpload: FileUpload): void {
        (async () => {
            try {
                try {
                    const urls = this.getFileDeleteUrls(fileUpload);
                    for (const url of urls) {
                        try {
                            await firstValueFrom(this.http.get(url));
                        } catch (e) {
                            this.logger.error({
                                message: 'Error while removing file from remote server',
                                error: e,
                                data: FileUpload.toLoggableObject(fileUpload),
                            });
                        }
                    }
                } catch (e) {
                    this.logger.error({
                        message: 'Error while getting URL for removing file from storage',
                        error: e,
                        data: FileUpload.toLoggableObject(fileUpload),
                    });
                }

            } catch (e) {
                this.logger.error({
                    message: 'Error while removing file from local storage',
                    error: e,
                    data: FileUpload.toLoggableObject(fileUpload),
                });
            }
        })();
    }

    private removeFileUploadFromTracker(tracker: FileUploadStatusTracker, fileUploadId: string) {
        tracker.removeFileUpload(fileUploadId);
        this.notifyFileUploadsListChanged();
    }

    private notifyFileUploadsListChanged() {
        const fileUploads = this.getFileUploads();
        this._fileUploadsListChanged.next(fileUploads);
    }

    private notifyFileUploadChanged(fileUpload: FileUpload) {
        this._fileUploadChanged.next(fileUpload);
    }

    public async removeAllFiles(fileUploads: FileUpload[]) {
        for (const fileUpload of fileUploads) {
            await this.removeFile(fileUpload);
        }
    }

    private getFileDeleteUrls(fileUpload: FileUpload): string[] {
        const metadata = fileUpload.endpointData.processorMetadataList[0];
        const domains = [
            metadata.downloadDomain,
            metadata.uploadDomain,
            metadata.processDomain,
        ];
        return uniq(domains).map(domain => {
            return ApiConfig.suffixDomainWithHttpsEdgeEndpoint(domain) + FILE_DELETE_ENDPOINT + fileUpload.id;
        });
    }

    public getFileUploads(): FileUpload[] {
        return flatten(this.fileUploadTrackers.map(t => t.getFileUploads())).filter(t => t);
    }

    private getFileUploadsById(fileUploadId: string): FileUpload[] {
        return this.getFileUploads().filter(f => f.id === fileUploadId);
    }

    private migrateFilesToPodTracker() {
        if (!this.podTracker) return;
        for (const tracker of this.fileUploadTrackers) {
            if (tracker === this.podTracker) continue;
            const files = tracker.getFileUploads();
            for (const file of files) {
                if (!this.podTracker.hasFileUpload(file.id)) {
                    this.podTracker.addFileUpload(file);
                }
                tracker.removeFileUpload(file.id);
            }
        }
    }

    private ensurePodTracker(): boolean {
        if (this.podTracker) { console.log('[FileManager] podTracker exists'); return true; }
        // Try sync read from ReplaySubject if settings haven't been consumed yet
        if (!this.useTaskScheduling) {
            this.settingsService.featureFlags.pipe(take(1)).subscribe(ff => {
                this.useTaskScheduling = ff?.useTaskScheduling ?? false;
                console.log('[FileManager] ensurePodTracker sync read: useTaskScheduling=', this.useTaskScheduling);
            });
        }
        if (!this.useTaskScheduling) { console.log('[FileManager] useTaskScheduling=false, skipping pod'); return false; }
        console.log('[FileManager] Creating pod tracker for', this.apiConfig.podEndpoint);
        this.podTracker = FileUploadStatusTracker.createForPod(this.apiConfig.podEndpoint);
        this.fileUploadTrackers.push(this.podTracker);
        return true;
    }

    /** Add FileUploads. We are not tracking UploadInProgress files */
    public addFileUpload(fileUpload: FileUpload) {
        this.addFileUploads([fileUpload]);
    }

    public addFileUploads(fileUploads: FileUpload[]) {
        // Only start HTTP polling if WebSocket is off. When useWebsockets=true,
        // the constructor already called initFileTrackingUsingWebSocket().
        // Without this guard, both paths run simultaneously (C1 bug).
        if (!this.useWebsockets) {
            this.initFileTracker();
        }
        fileUploads.forEach((fileUpload, index) => {
            if (this.ensurePodTracker()) {
                console.log('[FileManager] Adding file to pod tracker:', fileUpload.id);
                this.podTracker.addFileUpload(fileUpload);
            } else {
                console.log('[FileManager] Adding file to edge tracker:', fileUpload.id);
                let tracker = this.getFileUploadTracker(fileUpload);
                if (!tracker) {
                    tracker = FileUploadStatusTracker.createFromFileUpload(fileUpload);
                    if (tracker) {
                        this.fileUploadTrackers.push(tracker);
                    }
                }
                if (tracker) {
                    tracker.addFileUpload(fileUpload);
                }
            }
        });
        this.notifyFileUploadsListChanged();
    }

    private getFileUploadTracker(fileUpload: FileUpload) {
        return this.fileUploadTrackers.find(t => t.sameAs(fileUpload));
    }

    /** Re-track a file upload after its process domain changes (e.g., on retry) */
    public retrackFileUpload(fileUpload: FileUpload) {
        // Remove from ALL trackers that contain this file ID — the file may have
        // been added to an untracked tracker (null endpoint) before processDomain
        // was set, so matching by endpoint alone misses it.
        for (const tracker of this.fileUploadTrackers) {
            if (tracker.hasFileUpload(fileUpload.id)) {
                tracker.removeFileUpload(fileUpload.id);
            }
        }
        this.addFileUpload(fileUpload);
    }

    saveFileUploadToStorage(fileUpload: FileUpload) {
        this.storageService.store(StorageKeyPrefixeTypes.FILE_UPLOAD_KEY_PREFIX, fileUpload.id, fileUpload);
        const untractedTracker = this.fileUploadTrackers.find(t => !t.canTrack);
        if (untractedTracker) {
            const fileUploads = untractedTracker.getFileUploads();
            (fileUploads || []).forEach(fileUpload => {
                let canCreate = FileUploadStatusTracker.canCreateTrackedFileUploadTracker(fileUpload);
                if (canCreate) {
                    this.removeFileUploadFromTracker(untractedTracker, fileUpload.id);
                    this.addFileUpload(fileUpload);
                }
            });
        }
    }

    private saveFileUploadToStorageWithoutRecomputingUntrackedFileUploads(fileUpload: FileUpload) {
        this.storageService.store(StorageKeyPrefixeTypes.FILE_UPLOAD_KEY_PREFIX, fileUpload.id, fileUpload);
    }

    checkStatus(fileInfo: FileUpload) {
        if (!ConnectivityUtils.isOnline()) return Promise.resolve();
        return this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
            fileInfo.endpointData.processorMetadataList[0].processDomain,
            fileInfo.id,
        )
            .then(data => this.logger.trace7({ message: `Check fileUpload status.`, data }))
            .catch(err => this.logger.error({ message: 'Error checking fileUpload status.', error: err }));
    }

    /**
     *
     * @param _fileUpload to apply ws reply to a fileUpload on this session, fileUpload should be null.
     * There is a recovered version of same fileUpload on recoveredFileUploads, sending to that wouldnt update the UI
     * @param websocketResponse
     */
    public applyHistory(_fileUpload: FileUpload, websocketResponse: WebsocketResponse) {
        const fileUploads = _fileUpload && [_fileUpload] || this.getFileUploadsById(websocketResponse.uuid);
        if (fileUploads && fileUploads.length > 0) {
            fileUploads.forEach(fileUpload => {
                // Monotonic eventId guard — prevents progress bar regression.
                // eventIds are now stored on task documents from the edge's counter,
                // monotonic for both WebSocket and replay-history paths.
                const isTerminal = websocketResponse.processStage === ProcessStageTypes.COMPLETED;
                if (!isTerminal && websocketResponse.eventId && (fileUpload as any)._lastEventId
                    && websocketResponse.eventId <= (fileUpload as any)._lastEventId) {
                    return;
                }
                if (websocketResponse.eventId) {
                    (fileUpload as any)._lastEventId = websocketResponse.eventId;
                }

                // only update result with ws message it we are allowed to
                if (!fileUpload.discartWSReply) {
                    const isTerminalMessage = websocketResponse.processStage === ProcessStageTypes.COMPLETED;

                    // Once completed/failed, ignore all non-terminal messages
                    if (fileUpload.result && !fileUpload.result.error && !isTerminalMessage) {
                        return;
                    }

                    fileUpload.ws = websocketResponse;
                    if (!fileUpload.result || fileUpload.result.error || isTerminalMessage) {
                        fileUpload.result = websocketResponse.result && JSON.parse(websocketResponse.result);
                        // Patch edge domains from ProcessResult (set by ironman on completion)
                        if (fileUpload.result?.processDomain && fileUpload.endpointData?.processorMetadataList?.[0]) {
                            fileUpload.endpointData.processorMetadataList[0].processDomain = fileUpload.result.processDomain;
                        }
                        if (fileUpload.result?.downloadDomain && fileUpload.endpointData?.processorMetadataList?.[0]) {
                            fileUpload.endpointData.processorMetadataList[0].downloadDomain = fileUpload.result.downloadDomain;
                        }
                        this.saveFileUploadToStorageWithoutRecomputingUntrackedFileUploads(fileUpload);
                    } else {
                        if (!fileUpload.processEndTime) {
                            fileUpload.processEndTime = new Date();
                        }
                    }
                }

                if (fileUpload && fileUpload.result) {
                    const result = fileUpload.result;
                    if (websocketResponse.processStage === ProcessStageTypes.COMPLETED && websocketResponse.processStageSubState === ProcessStageSubStateTypes.COMPLETED) {
                        fileUpload.processEndTime = result.end;
                        fileUpload.processStartTime = result.start || fileUpload.processStartTime;
                        this.saveFileUploadToStorage(fileUpload);
                    }

                    if (websocketResponse.processStage === ProcessStageTypes.ZIP &&
                        (!fileUpload.zipResult || (fileUpload.zipResult && fileUpload.zipResult.processStageSubState !== ProcessStageSubStateTypes.COMPLETED))
                    ) {
                        fileUpload.zipResult = websocketResponse;
                        if (websocketResponse.processStageSubState === ProcessStageSubStateTypes.COMPLETED) {
                            this.saveFileUploadToStorageWithoutRecomputingUntrackedFileUploads(fileUpload);
                        }
                    }
                }
                this.notifyFileUploadChanged(fileUpload);
            });
        }
    }

    public stringifyAllUploadedFileUploads() {
        try {
            let fileArray = [];
            (this.getFileUploads() || []).forEach(fileUpload => {
                try {
                    fileArray.push(FileUpload.toLoggableObject(fileUpload));
                } catch (e) {
                    this.logger.error({
                        message: 'Failed to serialize a file upload',
                        error: e,
                    });
                }
            });
            return JSONUtils.safeStringify(fileArray);
        } catch (e) {
            this.logger.error({
                message: 'Failed to serialize file uploads',
                error: e,
            });
        }
    }
}
